import { customFieldRecordKey } from '@/features/custom-fields';
import {
  leadPipelineResponseSchema,
  partnerCreateDefaultsSchema,
  partnerListResponseSchema,
  partnerSalesSummarySchema,
  partnerSchema,
  supplierOrderListResponseSchema,
  supplierProductListResponseSchema,
  supplierProductSchema,
  type LeadPipelineResponse,
  type CustomFieldFilterValues,
  type LeadStageUpdateInput,
  type Partner,
  type PartnerConvertInput,
  type PartnerCreateInput,
  type PartnerCreateDefaults,
  type PartnerInsuranceInput,
  type PartnerListResponse,
  type PartnerListItem,
  type PartnerLockInput,
  type PartnerSalesSummary,
  type PartnerType,
  type PartnerUnlockInput,
  type PartnerUpdateInput,
  type SupplierOrder,
  type SupplierProduct,
  type SupplierProductInput,
  type SupplierProductUpdateInput,
} from '@erp/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiDownload, apiFetch, apiSend, saveBlob, type ApiError } from '@/lib/api-client';

export interface PartnerListParams {
  mapFilters?: Record<string, string>;
  page: number;
  pageSize: number;
  sort: string;
  type: PartnerType;
  q?: string | undefined;
  priceGroupIds?: string[];
  subdivisionCode?: string[];
  status?: string[];
  industry?: string[];
  industryCode?: string | undefined;
  tags?: string[];
  leadStatus?: string[];
  leadSource?: string[];
  supplierRating?: string[];
  countryCode?: string | undefined;
  vatIdStatus?: string[];
  riskStatus?: string[];
  customFieldFilters?: CustomFieldFilterValues | undefined;
}

export function buildPartnerQuery(params: PartnerListParams): string {
  const search = new URLSearchParams({
    page: String(params.page),
    pageSize: String(params.pageSize),
    sort: params.sort,
    type: params.type,
  });

  if (params.industryCode !== undefined && params.industryCode.trim() !== '') {
    search.set('industryCode', params.industryCode.trim());
  }

  if (params.q !== undefined && params.q.length > 0) {
    search.set('q', params.q);
  }

  const lists: ReadonlyArray<[string, string[] | undefined]> = [
    ['priceGroupIds', params.priceGroupIds],
    ['subdivisionCode', params.subdivisionCode],
    ['status', params.status],
    ['industry', params.industry],
    ['tags', params.tags],
    ['leadStatus', params.leadStatus],
    ['leadSource', params.leadSource],
    ['supplierRating', params.supplierRating],
    ['vatIdStatus', params.vatIdStatus],
    ['riskStatus', params.riskStatus],
  ];
  for (const [key, values] of lists) {
    if (values !== undefined && values.length > 0) {
      search.set(key, values.join(','));
    }
  }

  if (params.countryCode !== undefined && params.countryCode.length > 0) {
    search.set('countryCode', params.countryCode);
  }
  if (
    params.customFieldFilters !== undefined &&
    Object.keys(params.customFieldFilters).length > 0
  ) {
    search.set('customFieldFilters', JSON.stringify(params.customFieldFilters));
  }

  for (const [key, value] of Object.entries(params.mapFilters ?? {}))
    if (value) search.set(key, value);
  return search.toString();
}

export const partnerKeys = {
  all: ['partners'] as const,
  list: (params: PartnerListParams) => ['partners', 'list', params] as const,
  detail: (id: string) => ['partners', 'detail', id] as const,
  pipeline: () => ['partners', 'pipeline'] as const,
  createDefaults: () => ['partners', 'create-defaults'] as const,
  salesSummary: (id: string, dateFrom?: string, dateTo?: string) =>
    ['partners', 'detail', id, 'sales-summary', dateFrom, dateTo] as const,
  supplierProducts: (id: string) => ['partners', 'detail', id, 'supplier-products'] as const,
  purchaseOrders: (id: string) => ['partners', 'detail', id, 'purchase-orders'] as const,
};

export function usePartnerList(
  params: PartnerListParams,
  enabled = true,
): UseQueryResult<PartnerListResponse, ApiError> {
  return useQuery({
    enabled,
    queryKey: partnerKeys.list(params),
    queryFn: ({ signal }) =>
      apiFetch(`/api/v1/partners?${buildPartnerQuery(params)}`, partnerListResponseSchema, {
        signal,
      }),
    placeholderData: (previous) => previous,
  });
}

export function usePartner(id: string): UseQueryResult<Partner, ApiError> {
  return useQuery({
    queryKey: partnerKeys.detail(id),
    queryFn: () => apiFetch(`/api/v1/partners/${id}`, partnerSchema),
    enabled: id !== '',
  });
}

export function useLeadPipeline(): UseQueryResult<LeadPipelineResponse, ApiError> {
  return useQuery({
    queryKey: partnerKeys.pipeline(),
    queryFn: () => apiFetch('/api/v1/partners/lead-pipeline', leadPipelineResponseSchema),
  });
}

export function useCreatePartner(): UseMutationResult<Partner, ApiError, PartnerCreateInput> {
  const queryClient = useQueryClient();

  return useMutation<Partner, ApiError, PartnerCreateInput>({
    mutationFn: (input) =>
      apiFetch('/api/v1/partners', partnerSchema, { method: 'POST', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: partnerKeys.all });
    },
  });
}

export function usePartnerCreateDefaults(): UseQueryResult<PartnerCreateDefaults, ApiError> {
  return useQuery({
    queryKey: partnerKeys.createDefaults(),
    queryFn: () => apiFetch('/api/v1/partners/create-defaults', partnerCreateDefaultsSchema),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdatePartner(
  id: string,
): UseMutationResult<Partner, ApiError, PartnerUpdateInput> {
  const queryClient = useQueryClient();

  return useMutation<Partner, ApiError, PartnerUpdateInput>({
    mutationFn: (input) =>
      apiFetch(`/api/v1/partners/${id}`, partnerSchema, { method: 'PATCH', body: input }),
    onSuccess: (partner) => {
      void queryClient.invalidateQueries({ queryKey: customFieldRecordKey('CUSTOMER', id) });
      queryClient.setQueryData(partnerKeys.detail(id), partner);
      void queryClient.invalidateQueries({ queryKey: partnerKeys.all });
    },
  });
}

export function useDeletePartner(): UseMutationResult<void, ApiError, string> {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, string>({
    mutationFn: (id) => apiSend(`/api/v1/partners/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: partnerKeys.all });
    },
  });
}

interface PartnerBulkDeleteResult {
  deletedIds: string[];
  failures: { partner: PartnerListItem; error: unknown }[];
}

export function useBulkDeletePartners(): UseMutationResult<
  PartnerBulkDeleteResult,
  Error,
  PartnerListItem[]
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (partners: PartnerListItem[]): Promise<PartnerBulkDeleteResult> => {
      const result: PartnerBulkDeleteResult = { deletedIds: [], failures: [] };
      for (const partner of partners) {
        try {
          await apiSend(`/api/v1/partners/${partner.id}`, { method: 'DELETE' });
          result.deletedIds.push(partner.id);
        } catch (error) {
          result.failures.push({ partner, error });
        }
      }
      return result;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: partnerKeys.all });
    },
  });
}

export function useUpdateLeadStage(): UseMutationResult<
  Partner,
  ApiError,
  { id: string; input: LeadStageUpdateInput }
> {
  const queryClient = useQueryClient();

  return useMutation<Partner, ApiError, { id: string; input: LeadStageUpdateInput }>({
    mutationFn: ({ id, input }) =>
      apiFetch(`/api/v1/partners/${id}/lead-stage`, partnerSchema, {
        method: 'PATCH',
        body: input,
      }),
    onSuccess: (partner) => {
      queryClient.setQueryData(partnerKeys.detail(partner.id), partner);
      void queryClient.invalidateQueries({ queryKey: partnerKeys.all });
    },
  });
}

export function useConvertToCustomer(
  id: string,
): UseMutationResult<Partner, ApiError, PartnerConvertInput> {
  const queryClient = useQueryClient();

  return useMutation<Partner, ApiError, PartnerConvertInput>({
    mutationFn: (input) =>
      apiFetch(`/api/v1/partners/${id}/convert-to-customer`, partnerSchema, {
        method: 'POST',
        body: input,
      }),
    onSuccess: (partner) => {
      queryClient.setQueryData(partnerKeys.detail(id), partner);
      void queryClient.invalidateQueries({ queryKey: partnerKeys.all });
    },
  });
}

export function usePartnerSalesSummary(
  id: string,
  period: { dateFrom?: string | undefined; dateTo?: string | undefined } = {},
): UseQueryResult<PartnerSalesSummary, ApiError> {
  const search = new URLSearchParams();
  if (period.dateFrom !== undefined) search.set('dateFrom', period.dateFrom);
  if (period.dateTo !== undefined) search.set('dateTo', period.dateTo);
  return useQuery({
    queryKey: partnerKeys.salesSummary(id, period.dateFrom, period.dateTo),
    queryFn: () =>
      apiFetch(
        `/api/v1/partners/${id}/sales-summary?${search.toString()}`,
        partnerSalesSummarySchema,
      ),
    enabled: id !== '',
  });
}

// ---------------------------------------------------------------------------
// Risikomanagement und Warenkreditversicherung
// ---------------------------------------------------------------------------

export function useLockPartner(id: string): UseMutationResult<Partner, ApiError, PartnerLockInput> {
  const queryClient = useQueryClient();

  return useMutation<Partner, ApiError, PartnerLockInput>({
    mutationFn: (input) =>
      apiFetch(`/api/v1/partners/${id}/lock`, partnerSchema, { method: 'POST', body: input }),
    onSuccess: (partner) => {
      queryClient.setQueryData(partnerKeys.detail(id), partner);
      void queryClient.invalidateQueries({ queryKey: partnerKeys.all });
    },
  });
}

export function useUnlockPartner(
  id: string,
): UseMutationResult<Partner, ApiError, PartnerUnlockInput> {
  const queryClient = useQueryClient();

  return useMutation<Partner, ApiError, PartnerUnlockInput>({
    mutationFn: (input) =>
      apiFetch(`/api/v1/partners/${id}/unlock`, partnerSchema, { method: 'POST', body: input }),
    onSuccess: (partner) => {
      queryClient.setQueryData(partnerKeys.detail(id), partner);
      void queryClient.invalidateQueries({ queryKey: partnerKeys.all });
    },
  });
}

export function useUpdatePartnerInsurance(
  id: string,
): UseMutationResult<Partner, ApiError, PartnerInsuranceInput> {
  const queryClient = useQueryClient();

  return useMutation<Partner, ApiError, PartnerInsuranceInput>({
    mutationFn: (input) =>
      apiFetch(`/api/v1/partners/${id}/insurance`, partnerSchema, {
        method: 'PATCH',
        body: input,
      }),
    onSuccess: (partner) => {
      queryClient.setQueryData(partnerKeys.detail(id), partner);
      void queryClient.invalidateQueries({ queryKey: partnerKeys.all });
    },
  });
}

export function usePartnerExport(): UseMutationResult<
  void,
  ApiError,
  { params: PartnerListParams; format: 'csv' | 'xlsx' }
> {
  return useMutation<void, ApiError, { params: PartnerListParams; format: 'csv' | 'xlsx' }>({
    mutationFn: async ({ params, format }) => {
      const query = buildPartnerQuery({ ...params, page: 1, pageSize: 200 });
      const result = await apiDownload(
        `/api/v1/partners/export?${query}&format=${format}`,
        `geschaeftspartner.${format}`,
      );
      saveBlob(result);
    },
  });
}

// ---------------------------------------------------------------------------
// Einkauf
// ---------------------------------------------------------------------------

export function useSupplierProducts(id: string): UseQueryResult<SupplierProduct[], ApiError> {
  return useQuery({
    queryKey: partnerKeys.supplierProducts(id),
    queryFn: async () => {
      const response = await apiFetch(
        `/api/v1/partners/${id}/supplier-products`,
        supplierProductListResponseSchema,
      );
      return response.data;
    },
    enabled: id !== '',
  });
}

export function useCreateSupplierProduct(
  partnerId: string,
): UseMutationResult<SupplierProduct, ApiError, SupplierProductInput> {
  const queryClient = useQueryClient();

  return useMutation<SupplierProduct, ApiError, SupplierProductInput>({
    mutationFn: (input) =>
      apiFetch(`/api/v1/partners/${partnerId}/supplier-products`, supplierProductSchema, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: partnerKeys.supplierProducts(partnerId) });
    },
  });
}

export function useUpdateSupplierProduct(
  partnerId: string,
): UseMutationResult<
  SupplierProduct,
  ApiError,
  { supplierProductId: string; input: SupplierProductUpdateInput }
> {
  const queryClient = useQueryClient();

  return useMutation<
    SupplierProduct,
    ApiError,
    { supplierProductId: string; input: SupplierProductUpdateInput }
  >({
    mutationFn: ({ supplierProductId, input }) =>
      apiFetch(
        `/api/v1/partners/${partnerId}/supplier-products/${supplierProductId}`,
        supplierProductSchema,
        { method: 'PATCH', body: input },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: partnerKeys.supplierProducts(partnerId) });
    },
  });
}

export function useDeleteSupplierProduct(
  partnerId: string,
): UseMutationResult<void, ApiError, string> {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, string>({
    mutationFn: (supplierProductId) =>
      apiSend(`/api/v1/partners/${partnerId}/supplier-products/${supplierProductId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: partnerKeys.supplierProducts(partnerId) });
    },
  });
}

export function useSupplierOrders(id: string): UseQueryResult<SupplierOrder[], ApiError> {
  return useQuery({
    queryKey: partnerKeys.purchaseOrders(id),
    queryFn: async () => {
      const response = await apiFetch(
        `/api/v1/partners/${id}/purchase-orders`,
        supplierOrderListResponseSchema,
      );
      return response.data;
    },
    enabled: id !== '',
  });
}
